/**
 * LA COMMANDE FOURNISSEUR — détail (maquette `acCmdOn`), « Créer un BL » depuis la commande
 * (`acCbOn`). La nouvelle commande vit dans `order-editor.tsx`.
 *
 * La commande ne touche JAMAIS au stock : son reçu se dérive des BL rattachés, et c'est vers eux que
 * la modale renvoie (« Ouvrir »). Les écarts figés à l'intégration s'affichent, ils ne se réécrivent pas.
 *
 * ⚠️ LA TRANSMISSION SE DIT (D1) : « Envoyée le … à … » seulement si l'e-mail est parti, « Validée — à transmettre »
 *    pour une saisie jamais envoyée. Le bon de commande est le PDF du SERVEUR — le même octet que la pièce jointe.
 * ⚠️ L'ENVOI N'EST OFFERT QUE SI LA MESSAGERIE DU MAGASIN EST RÉGLÉE : sinon la modale dit pourquoi, au lieu
 *    d'un bouton qui échouerait à chaque clic.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { confirm, Icon, Modal, showToast, Trans, useLatest } from '@orgazone/ui-web';
import { api, type CommandeDetaillee, type SourceDuBon } from '../api';
import { fieldLabel } from '../vocabulary';
import {
  dateCourte, ETATS, heureCourte, MONO, orderStatusTone, orderSubtitle, orderTransmission,
  StatusPill, Source, SourceCmd,
} from './receptions-shared';
import { BonModal } from './receptions-bon';
import { t } from '../i18n';

export function CommandeModal({ numero, onClose, onBonCree }: {
  numero: string; onClose: () => void;
  /** Un BL vient d'être créé depuis cette commande — le parent bascule sur l'onglet BL et l'ouvre. */
  onBonCree: (numeroBon: string) => void;
}) {
  const [commande, setCommande] = useState<CommandeDetaillee | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [blOuvert, setBlOuvert] = useState<string | null>(null);
  const [creerBl, setCreerBl] = useState(false);
  const [mail, setMail] = useState<{ ready: boolean; why: string } | null>(null);
  const [sending, setSending] = useState(false);

  // ⚠️ UNE LECTURE RATÉE N'EST PAS « PAS DE MESSAGERIE » : on ne sait rien, donc on n'offre ni n'explique l'envoi.
  useEffect(() => {
    let alive = true;
    void api.mailReadiness().then((r) => { if (alive) setMail(r); }).catch(() => { /* la modale reste muette sur l'envoi */ });
    return () => { alive = false; };
  }, []);

  // ⚠️ LE DERNIER GESTE LANCÉ ÉCRIT LA COMMANDE : la relecture et la clôture rendent chacune la commande entière — une
  //    lecture revenue après la clôture la ferait réapparaître ouverte, reliquat compris.
  const latest = useLatest();
  const reload = useCallback(async () => {
    const isLatest = latest();
    try {
      const c = await api.order(numero);
      if (isLatest()) { setCommande(c); setFailure(null); }
    } catch (e) {
      if (isLatest()) setFailure(e instanceof Error ? e.message : String(e));
    }
  }, [numero, latest]);
  useEffect(() => { void reload(); }, [reload]);

  const cloturer = useCallback(async () => {
    if (!commande) return;
    const ok = await confirm({
      title: t('Clôturer la commande {numero} ?', { numero }),
      message: t('{n} pièce commandée n’est pas livrée et ne le sera plus : le reliquat est ABANDONNÉ. La commande garde la trace, elle ne se réécrit pas.', { n: commande.resume.remaining }),
      confirmLabel: t('Clôturer — reliquat abandonné'), tone: 'info',
    });
    if (!ok) return;
    const isLatest = latest();
    try {
      const c = await api.closeOrder(numero);
      if (isLatest()) setCommande(c);
      showToast(t('Commande clôturée — reliquat abandonné'));
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err'); }
  }, [commande, numero, latest]);

  /** ENVOYER (ou renvoyer) le bon de commande — confirmé d'abord : un e-mail parti ne se rattrape pas. */
  const send = useCallback(async () => {
    if (!commande || sending) return;
    const again = commande.sentAt != null;
    const ok = await confirm({
      title: again ? t('Renvoyer le bon de commande {numero} ?', { numero }) : t('Envoyer le bon de commande {numero} ?', { numero }),
      message: again
        ? t('Il a déjà été envoyé le {date} à {heure} à {to}. Il repart en PDF à l’adresse de commande de la fiche fournisseur, depuis la messagerie du magasin.', {
          date: dateCourte(commande.sentAt), heure: heureCourte(commande.sentAt!), to: commande.sentTo ?? '—',
        })
        : t('Il part en PDF à l’adresse de commande de la fiche fournisseur, depuis la messagerie du magasin.'),
      confirmLabel: again ? t('Renvoyer') : t('Envoyer'), tone: 'info',
    });
    if (!ok) return;
    setSending(true);
    try {
      const r = await api.sendOrder(numero);
      showToast(t('Bon de commande envoyé à {to}', { to: r.sentTo }), 'ok');
      await reload();
    } catch (e) {
      // ⚠️ LE MOTIF RÉEL du refus (transport, fiche sans adresse) : la commande reste « à transmettre ».
      showToast(e instanceof Error ? e.message : String(e), 'err', 8000);
    } finally { setSending(false); }
  }, [commande, numero, reload, sending]);

  if (failure) {
    return (
      <Modal open onClose={onClose} title={t('Commande {numero}', { numero })} width="md">
        <p role="alert" style={{ margin: 0, color: 'var(--danger-ink)', fontSize: '13px' }}>{t('La commande n’a pas pu être lue : {failure}', { failure })}</p>
      </Modal>
    );
  }
  if (!commande) return <Modal open onClose={onClose} title={t('Commande {numero}', { numero })} width="md"><p style={{ margin: 0, color: 'var(--ink-500)' }}>{t('Lecture…')}</p></Modal>;

  const close = commande.resume.etat === 'closed';
  const transmission = orderTransmission(commande);
  const isDraft = commande.resume.etat === 'draft';
  const GRID = 'minmax(0,1fr) 58px 64px 64px 86px';
  // « attendu N, reçu M » — le bandeau ambre des écarts figés (maquette acCmdEcL).
  const variances = commande.variances
    .map((v) => t('{v}{v2} : attendu {expected}, reçu {checked}', { v: v.name ?? v.gencode, v2: v.size ? ` ${v.size}` : '', expected: v.expected, checked: v.checked }))
    .join(' · ');

  return (
    <>
      <Modal open onClose={onClose} title={commande.number} width="xl" icon="inbound"
        subtitle={orderSubtitle(commande)}
        meta={<span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}><SourceCmd s={commande.source} /><StatusPill tone={orderStatusTone(commande)} /></span>}
        footer={(
          <>
            {(commande.resume.etat === 'partial' || commande.resume.etat === 'waiting') && (
              <button type="button" onClick={() => void cloturer()}
                style={{ height: 38, padding: '0 14px', border: '1px solid var(--amber)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--amber-fg)', fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer' }}
              >{t('Clôturer le reliquat')}</button>
            )}
            {!isDraft && (
              <a className="btn-ghost" href={api.orderPdfUrl(commande.number)} target="_blank" rel="noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
                <Icon name="file" size={14} />  {t('Bon de commande (PDF)')}
              </a>
            )}
            {!isDraft && mail?.ready && (
              <button type="button" className="btn-ghost" onClick={() => void send()} aria-disabled={sending || undefined}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Icon name="mail" size={14} />  {sending ? t('Envoi…') : commande.sentAt != null ? t('Renvoyer au fournisseur') : t('Envoyer au fournisseur')}
              </button>
            )}
            <div style={{ flex: 1 }} />
            {!close && (
              <button type="button" onClick={() => setCreerBl(true)}
                style={{ height: 38, padding: '0 15px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-500)', color: '#fff', fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer', boxShadow: '0 6px 18px -8px #2bc9a3' }}
              >{t('Créer un BL')}</button>
            )}
            <button type="button" onClick={onClose}
              style={{ height: 38, padding: '0 15px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-900)', color: 'var(--paper)', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>{t('Fermer')}</button>
          </>
        )}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
          {transmission === 'sent' && (
            <p style={{ margin: 0, padding: '9px 12px', borderRadius: 'var(--r-sm)', background: 'var(--teal-100)', color: 'var(--teal-800)', fontSize: '12.5px', fontWeight: 600 }}>
              {t('Envoyée le {date} à {heure} à {to}', { date: dateCourte(commande.sentAt), heure: heureCourte(commande.sentAt!), to: commande.sentTo ?? '—' })}
            </p>
          )}
          {transmission === 'toSend' && (
            <p style={{ margin: 0, padding: '9px 12px', borderRadius: 'var(--r-sm)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: '12.5px', fontWeight: 600, lineHeight: 1.5 }}>
              {mail?.ready === false
                ? t('Validée — à transmettre : elle n’a pas été envoyée au fournisseur. L’envoi par e-mail n’est pas possible : {why} Téléchargez le bon de commande pour la transmettre vous-même.', { why: mail.why })
                : t('Validée — à transmettre : elle n’a pas été envoyée au fournisseur.')}
            </p>
          )}
          <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflowX: 'auto' }}>
            <div style={{ minWidth: 560 }}>
              <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 8, padding: '9px 14px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)', fontSize: '10px', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
                <span>{t('Article')}</span><span style={{ textAlign: 'center' }}>{fieldLabel('size')}</span><span style={{ textAlign: 'right' }}>{t('Cmdé')}</span>
                <span style={{ textAlign: 'right' }}>{t('Reçu')}</span><span style={{ textAlign: 'right' }}>{t('Reste')}</span>
              </div>
              {commande.lines.map((l) => {
                const reste = Math.max(0, l.orderedQty - l.receivedQty);
                const abandonne = close && reste > 0;
                return (
                  <div key={l.gencode} style={{ display: 'grid', gridTemplateColumns: GRID, gap: 8, padding: '9px 14px', alignItems: 'center', borderBottom: '1px solid var(--ink-100)', fontSize: '12.5px' }}>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.name ?? l.gencode}{l.color ? ` · ${l.color}` : ''}</span>
                      <span style={{ display: 'block', fontFamily: MONO, fontSize: '10px', color: 'var(--ink-400)' }}>{l.gencode}</span>
                    </span>
                    <span style={{ textAlign: 'center', fontFamily: MONO, fontSize: '11.5px', color: 'var(--ink-600)' }}>{l.size ?? '—'}</span>
                    <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--ink-600)' }}>{l.orderedQty}</span>
                    <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: l.receivedQty > 0 ? 'var(--teal-800)' : 'var(--ink-300)' }}>{l.receivedQty}</span>
                    <span style={{
                      textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700,
                      color: reste === 0 ? 'var(--ink-400)' : abandonne ? 'var(--amber-fg)' : 'var(--ink-900)',
                      textDecoration: abandonne ? 'line-through' : 'none',
                    }}>{abandonne ? t('{n} abandonné', { n: reste }) : reste}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
            <div style={{ padding: '9px 14px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)', fontSize: '10.5px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>{t('BL rattachés')}</div>
            {commande.deliveryNoteRows.map((bx) => (
              <button key={bx.number} type="button" title={t('Ouvrir ce BL')} onClick={() => setBlOuvert(bx.number)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 14px', border: 'none', borderBottom: '1px solid var(--ink-100)', background: 'transparent', fontSize: '12.5px', textAlign: 'left', cursor: 'pointer' }}>
                <span style={{ fontFamily: MONO, fontSize: '11px', fontWeight: 700, color: 'var(--ink-800)' }}>{bx.number}</span>
                <Source s={bx.source as SourceDuBon} />
                <span style={{ flex: 1, minWidth: 0, color: 'var(--ink-600)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {ETATS[bx.etat] ? t(ETATS[bx.etat].l) : bx.etat}
                </span>
                <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'var(--ink-900)', flexShrink: 0 }}>{bx.received} / {bx.expected}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: 24, padding: '0 9px', borderRadius: 'var(--r-xs)', border: '1px solid var(--ink-200)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}>
                  
                  {t('Ouvrir')}<Icon name="chevronR" size={11} />
                </span>
              </button>
            ))}
            {commande.deliveryNoteRows.length === 0 && (
              <div style={{ padding: 14, fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Aucune livraison reçue pour l’instant.')}</div>
            )}
          </div>

          {variances && (
            <div style={{ padding: '12px 14px', borderRadius: 'var(--r-sm)', background: 'var(--amber-bg)', border: '1px solid var(--amber)', fontSize: '12.5px', color: 'var(--ink-800)', lineHeight: 1.6 }}>
              <Trans k='<1>Écarts figés à l’intégration :</1> {variances} — corrigés au contrôle du BL, entrés en stock au réel. La commande garde la trace, elle ne se réécrit pas.' vars={{ variances }} parts={[(x) => <strong>{x}</strong>]} /></div>
          )}
        </div>
      </Modal>
      {creerBl && (
        <CbModal commande={commande} onClose={() => setCreerBl(false)}
          onCree={(n) => { setCreerBl(false); onBonCree(n); }} />
      )}
      {blOuvert && <BonModal numero={blOuvert} onClose={() => { setBlOuvert(null); void reload(); }} />}
    </>
  );
}

// ── « CRÉER UN BL » depuis la commande — reste complet ou vide (maquette `acCb`) ──────────────────
function CbModal({ commande, onClose, onCree }: { commande: CommandeDetaillee; onClose: () => void; onCree: (numero: string) => void }) {
  const restes = useMemo(() => commande.lines
    .filter((l) => l.known)
    .map((l) => ({ gencode: l.gencode, reste: Math.max(0, l.orderedQty - l.receivedQty) }))
    .filter((l) => l.reste > 0), [commande]);
  const ral = restes.reduce((n, l) => n + l.reste, 0);
  const [mode, setMode] = useState<'solde' | 'vide'>(ral > 0 ? 'solde' : 'vide');
  const [numero, setNumero] = useState('');
  const [writing, setWriting] = useState(false);

  const MODES = [
    { k: 'solde' as const, t: t('Reste à livrer complet ({n} pièce)', { n: ral }), d: t('Les lignes attendues sont préremplies — vous contrôlez ligne à ligne.'), off: ral <= 0 },
    { k: 'vide' as const, t: t('BL vide — je scanne à la main'), d: t('Le BL naît sans ligne : bipez les pièces reçues, le scan est le réel.'), off: false },
  ];

  const create = async () => {
    if (writing) return;
    setWriting(true);
    try {
      // ⚠️ Les lignes du mode « solde » ne portent PAS de prix : le serveur applique l'échelle D5 —
      //    la ligne ORDRSP de cette commande passe devant tout, et la ligne le DIT (badge CMDE).
      const bon = await api.createDelivery({
        number: numero.trim() || undefined,
        supplierId: commande.supplierId, supplierLabel: commande.supplierLabel,
        orderRef: commande.number,
        source: mode === 'vide' ? 'scan' : 'manual',
        lines: mode === 'solde' ? restes.map((l) => ({ gencode: l.gencode, expectedQty: l.reste })) : [],
      });
      showToast(mode === 'solde'
        ? t('{number} créé depuis {number2} — contrôlez {pieces}', { number: bon.number, number2: commande.number, pieces: t('les {n} pièces attendues', { n: ral }) })
        : t('{number} créé — scannez les pièces reçues, chaque bip est le réel', { number: bon.number }));
      onCree(bon.number);
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
      setWriting(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={t('Créer un BL')} width="sm"
      subtitle={t('Rattaché à {number}.', { number: commande.number })}
      footer={(
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>{t('Annuler')}</button>
          <div style={{ flex: 1 }} />
          <button type="button" onClick={() => void create()} aria-disabled={writing || undefined}
            style={{ height: 36, padding: '0 16px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-500)', color: '#fff', fontSize: '13px', fontWeight: 700, boxShadow: '0 6px 18px -8px #2bc9a3', cursor: writing ? 'progress' : 'pointer' }}
          >{writing ? t('Création…') : mode === 'vide' ? t('Créer et scanner') : t('Créer et contrôler')}</button>
        </>
      )}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label>
          <span style={{ display: 'block', fontSize: '11.5px', fontWeight: 500, color: 'var(--ink-500)', marginBottom: 5 }}><Trans k='N° du BL <1>— vide : numéroté automatiquement</1>' parts={[(x) => <span style={{ color: 'var(--ink-400)' }}>{x}</span>]} /></span>
          <input value={numero} onChange={(e) => setNumero(e.target.value.toUpperCase())}
            style={{ width: '100%', height: 38, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', fontSize: '13px', fontWeight: 700, fontFamily: MONO, color: 'var(--ink-900)', outline: 'none', boxSizing: 'border-box' }} />
        </label>
        <div>
          <span style={{ display: 'block', fontSize: '11.5px', fontWeight: 500, color: 'var(--ink-500)', marginBottom: 6 }}>{t('Contenu du BL')}</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {MODES.map((md) => {
              const on = mode === md.k;
              return (
                <button aria-pressed={on} key={md.k} type="button" onClick={() => { if (!md.off) setMode(md.k); }} aria-disabled={md.off || undefined}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', textAlign: 'left', cursor: md.off ? 'not-allowed' : 'pointer',
                    border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-sm)',
                    background: on ? 'var(--teal-100)' : 'var(--paper)', opacity: md.off ? 0.45 : 1,
                  }}>
                  <span style={{ width: 16, height: 16, borderRadius: 'var(--r-pill)', border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-300)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxSizing: 'border-box', background: 'var(--paper)', marginTop: 1 }}>
                    {on && <span style={{ width: 8, height: 8, borderRadius: 'var(--r-pill)', background: 'var(--teal-500)' }} />}
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: 'var(--ink-900)' }}>{md.t}</span>
                    <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--ink-500)', marginTop: 1, lineHeight: 1.4 }}>{md.d}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </Modal>
  );
}
